import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Feather, ThermometerSnowflake, Ban, Microscope,
  FileCheck, Lock, FlaskConical, Flame, Zap, Layers, ChevronRight,
} from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Callout, DontList } from "@/components/Content";
import { CinematicSection } from "@/components/CinematicSection";
import { ASSETS } from "@/lib/api";
import { Seo, breadcrumbSchema } from "@/components/Seo";

const TRUST_NAV = [
  { label: "Our Process", to: "/our-process" },
  { label: "Chain Of Custody", to: "/our-process" },
  { label: "Confidentiality", to: "/confidentiality" },
  { label: "Shipping Protocol", to: "/shipping-insurance" },
  { label: "Insurance", to: "/shipping-insurance" },
  { label: "What We Will Not Do", to: "/our-process" },
  { label: "Grading Disclaimer", to: "/results" },
  { label: "Results Methodology", to: "/results" },
  { label: "Submission Safety", to: "/how-it-works" },
  { label: "Card Services", to: "/card-services" },
  { label: "Comic Services", to: "/comic-services" },
  { label: "FAQ", to: "/faq" },
  { label: "Terms", to: "/terms" },
];

const ICON_ROW = [
  { icon: Feather, label: "Gentle By Design" },
  { icon: ThermometerSnowflake, label: "No Direct Heat" },
  { icon: Ban, label: "No Harsh Chemical Treatments" },
  { icon: Microscope, label: "Individual Evaluation" },
  { icon: FileCheck, label: "Documented Handling" },
  { icon: Lock, label: "Confidential Service" },
];

const PRINCIPLES = [
  {
    icon: FlaskConical,
    title: "NO HARSH CHEMICAL TREATMENTS",
    body: [
      "CardScience HQ does not use harsh chemical treatments on collectibles as part of our standard process.",
      "Our approach is built around responsible handling and preservation-minded decision making.",
    ],
  },
  {
    icon: Flame,
    title: "NO DIRECT HEAT",
    body: [
      "We do not apply direct heat to cards as part of our standard process.",
      "We do not believe a collectible should be exposed to unnecessary thermal risk simply to force a cosmetic result.",
    ],
  },
  {
    icon: Zap,
    title: "NO AGGRESSIVE SHORTCUTS",
    body: [
      "We do not force surfaces, corners, edges or materials beyond what we believe can be responsibly handled.",
      "If achieving a particular result would require doing something we believe places the collectible at unnecessary risk, we stop.",
    ],
  },
  {
    icon: Layers,
    title: "NO ONE-SIZE-FITS-ALL PROCESS",
    body: [
      "A vintage baseball card is not the same as a modern Pokémon card.",
      "A chrome sports card is not the same as an older paper trading card.",
      "A comic book is not a trading card.",
      "Different materials require different judgment. Every collectible is evaluated independently.",
    ],
  },
];

const DEFECTS = [
  "Scratches", "Dents", "Creases", "Print defects", "Surface defects", "Edge wear",
  "Corner wear", "Centering issues", "Staining", "Manufacturing defects",
  "Existing damage", "Other condition issues",
];

const SECTIONS = [
  { id: "gentle-by-design", label: "Gentle By Design" },
  { id: "will-not-do", label: "What We Will Not Do" },
  { id: "our-standard", label: "Our Standard" },
  { id: "limitations", label: "Limitations" },
  { id: "closing", label: "Our Promise" },
];

function TrustSectionNav() {
  const [active, setActive] = useState("gentle-by-design");
  useEffect(() => {
    const onScroll = () => {
      let cur = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top - 150 <= 0) cur = s.id;
      }
      setActive(cur);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 116;
    window.scrollTo({ top: y, behavior: "smooth" });
  };
  return (
    <div data-testid="trust-section-nav" className="sticky top-[62px] z-30 bg-[#060608]/85 backdrop-blur-md border-y border-[color:var(--line)]">
      <div className="mx-auto max-w-[1100px] px-3">
        <div className="flex gap-1 overflow-x-auto no-scrollbar py-2.5">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`tc-jump-${s.id}`}
              onClick={() => go(s.id)}
              className={`whitespace-nowrap text-xs font-tech tracking-wide px-3.5 py-2 rounded-full border transition-colors ${
                active === s.id
                  ? "border-[color:var(--cyan)] text-[color:var(--cyan)]"
                  : "border-transparent text-[color:var(--chrome-2)] hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TrustCenter() {
  return (
    <div data-testid="trust-center-page" className="bg-ink">
      <Seo
        title="Trust Center — Our Standards & Confidentiality | CardScience HQ"
        description="Trust is part of the process. Learn how CardScience HQ protects your collectible, your privacy and your submission — gentle-by-design principles, documented handling and what we refuse to do."
        path="/trust-center"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Trust Center", path: "/trust-center" }])}
      />
      <CinematicSection
        image={ASSETS.labBg}
        contain={false}
        reverse
        alt="Secure, documented handling inside the CardScience HQ laboratory"
        label="Trust Center"
        headline="TRUST IS PART"
        accent="OF THE PROCESS."
        sentence="Collectors send us items that may be valuable, rare, sentimental or irreplaceable. See exactly how we protect your collectible, your privacy and your submission — with realistic expectations, never guaranteed grades."
        primaryTo="/submit"
        primaryLabel="SUBMIT A COLLECTIBLE"
        secondaryTo="/our-process"
        secondaryLabel="Our Process"
      />

      <TrustSectionNav />

      <Wrap className="py-14">
        <Prose>
          <P>The Trust Center explains our operating standards, documentation procedures, shipping requirements, insurance requirements, confidentiality policies and the principles behind our process.</P>
          <P>We will never publish every proprietary detail of our laboratory methods.</P>
          <P className="text-[color:var(--chrome-1)] font-medium">But we will tell you what matters most:</P>
          <div className="grid sm:grid-cols-3 gap-3">
            {["How we protect the collectible.", "How we protect the customer.", "And what we refuse to do."].map((t) => (
              <div key={t} className="rounded-xl holo-border px-4 py-4 text-sm text-[color:var(--chrome-1)]">{t}</div>
            ))}
          </div>
        </Prose>

        {/* Trust Center navigation */}
        <div className="mt-12">
          <p className="eyebrow mb-4">Explore The Trust Center</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {TRUST_NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`trust-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="group flex items-center justify-between gap-2 rounded-xl border border-[color:var(--line)] glass px-4 py-3.5 text-sm text-[color:var(--chrome-1)] hover:border-[color:var(--cyan)]/50 transition-colors"
              >
                <span>{n.label}</span>
                <ChevronRight size={16} className="text-[color:var(--chrome-3)] group-hover:text-[color:var(--cyan)] group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
        </div>

        {/* Icon trust row */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {ICON_ROW.map((t) => (
            <div key={t.label} className="flex flex-col items-center text-center gap-2.5 rounded-2xl border border-[color:var(--line)] py-5 px-3">
              <t.icon size={24} className="text-[color:var(--cyan)]" />
              <span className="font-tech text-[11px] tracking-wide text-[color:var(--chrome-2)] leading-tight">{t.label}</span>
            </div>
          ))}
        </div>
      </Wrap>

      {/* GENTLE BY DESIGN */}
      <div id="gentle-by-design" className="border-y border-[color:var(--line)] bg-[#0b0b0e] noise">
        <div className="mx-auto max-w-[1100px] px-5 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow mb-4">Our Process — Gentle By Design</p>
            <h2 className="h-display text-4xl sm:text-5xl leading-[1.03]">
              <span className="text-chrome">IT'S NOT THE SOLUTION.</span><br />
              <span className="text-holo">IT'S THE PROCESS.</span>
            </h2>
            <p className="text-base text-[color:var(--chrome-2)] mt-5 leading-relaxed">
              Anyone can look for a &ldquo;magic cleaner.&rdquo; CardScience HQ is built around understanding the collectible itself.
            </p>
          </div>
          <div className="relative rounded-3xl overflow-hidden holo-glow">
            <img src={ASSETS.hero} alt="Collectible under laboratory inspection" className="w-full h-full object-cover aspect-[4/3]" />
            <span className="absolute bottom-5 left-6 font-script text-2xl text-white drop-shadow-lg -rotate-3">The collectible determines the process.</span>
          </div>
        </div>

        <Wrap className="pb-16">
          <Prose className="max-w-[760px]">
            <P>There is a common misconception in the hobby that better-looking cards require a secret chemical, special solution or aggressive treatment.</P>
            <P>That is not how CardScience HQ operates.</P>
            <div className="py-2 space-y-1">
              <p className="h-display text-xl sm:text-2xl text-chrome">Our advantage is not a bottle.</p>
              <p className="h-display text-xl sm:text-2xl text-holo">Our advantage is our process.</p>
            </div>
            <div className="rounded-2xl holo-border p-6 space-y-1.5 text-[color:var(--chrome-1)]">
              <p>It is knowing what to touch.</p>
              <p>Knowing what not to touch.</p>
              <p>Knowing how much is enough.</p>
              <p className="text-[color:var(--chrome-3)] pt-1">And most importantly:</p>
              <p className="text-holo font-display text-lg">Knowing when to stop.</p>
            </div>
            <P>Every collectible is evaluated individually.</P>
            <P>Different cards may have different surfaces, coatings, finishes, ages, manufacturing characteristics and existing condition issues.</P>
            <P>Because of that, CardScience HQ does not believe in forcing every collectible through the same treatment.</P>
            <P className="text-[color:var(--chrome-1)] font-medium">The collectible determines the process. The process does not dictate what happens to the collectible.</P>
          </Prose>
        </Wrap>
      </div>

      {/* NON-NEGOTIABLE PRINCIPLES */}
      <div id="will-not-do">
      <Wrap className="py-16">
        <p className="eyebrow mb-3">Non-Negotiable Process Principles</p>
        <H2>WHAT WE WILL NOT DO</H2>
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {PRINCIPLES.map((p) => (
            <Callout key={p.title} className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl holo-border flex items-center justify-center shrink-0">
                  <p.icon size={20} className="text-[color:var(--cyan)]" />
                </div>
                <h3 className="font-display font-black text-base text-chrome tracking-tight">{p.title}</h3>
              </div>
              <div className="space-y-2">
                {p.body.map((b, i) => <P key={i} className="text-sm">{b}</P>)}
              </div>
            </Callout>
          ))}
        </div>
      </Wrap>
      </div>

      {/* SIGNATURE STATEMENT */}
      <div className="border-y border-[color:var(--line)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(60% 120% at 50% 0%, rgba(155,92,255,0.35), transparent 60%)" }} />
        <div className="relative mx-auto max-w-[900px] px-5 py-16 text-center">
          <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            <span className="text-chrome">THE SECRET ISN'T A BOTTLE.</span><br />
            <span className="text-holo">THE SECRET IS KNOWING THE COLLECTIBLE.</span>
          </h2>
          <div className="mt-8 max-w-xl mx-auto space-y-2 text-[color:var(--chrome-2)]">
            <p>Experience is knowing what can potentially be improved.</p>
            <p>Judgment is knowing what should be left alone.</p>
            <p className="text-[color:var(--chrome-1)] font-medium">CardScience HQ values both.</p>
          </div>
        </div>
      </div>

      {/* OUR STANDARD */}
      <Wrap className="py-16" >
        <div id="our-standard" />
        <p className="eyebrow mb-3">Our Standard</p>
        <H2>
          BETTER THAN YESTERDAY.<br />
          <span className="text-holo">NEVER AT THE EXPENSE OF THE COLLECTIBLE.</span>
        </H2>
        <Prose className="mt-6 max-w-[760px]">
          <P>Our goal is not to transform a collectible into something it was never meant to be.</P>
          <P>Our goal is to help an appropriate collectible present as the best version of what it already is.</P>
          <P>That may mean removing appropriate surface contamination. It may mean careful preparation. It may mean preservation-oriented handling.</P>
          <P className="text-[color:var(--chrome-1)] font-medium">And sometimes it means telling the customer: Leave this one alone.</P>
        </Prose>
        <Callout holo className="mt-8 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
          <span className="h-display text-holo text-5xl sm:text-6xl leading-none shrink-0">30%+</span>
          <div className="space-y-1.5">
            <P className="text-[color:var(--chrome-1)]">More than 30% of the cards we evaluate are turned away.</P>
            <P className="text-sm">We would rather decline a job than perform work we do not believe is appropriate.</P>
          </div>
        </Callout>
      </Wrap>

      {/* IMPORTANT LIMITATIONS */}
      <div id="limitations" className="border-t border-[color:var(--line)] bg-[#0b0b0e]">
        <Wrap className="py-16">
          <p className="eyebrow mb-3">Important Limitations</p>
          <H2>CLEANER DOES NOT MEAN PERFECT.</H2>
          <P className="mt-6">A cleaned or prepared card may still contain:</P>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-5">
            {DEFECTS.map((d) => (
              <div key={d} className="rounded-lg border border-[color:var(--line)] px-3 py-2.5 text-sm text-[color:var(--chrome-2)] flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--magenta)] shrink-0" /> {d}
              </div>
            ))}
          </div>
          <div className="mt-8">
            <DontList items={[
              "CardScience HQ does not claim that cleaning eliminates every defect.",
              "We do not guarantee that a card will become gradeable.",
              "We do not guarantee that a card will receive a higher grade.",
              "We do not guarantee a PSA 10.",
              "We do not guarantee monetary appreciation.",
            ]} />
          </div>
          <P className="mt-6 text-sm text-[color:var(--chrome-3)]">Independent grading companies determine grades under their own standards.</P>
        </Wrap>
      </div>

      {/* CLOSING */}
      <div id="closing" className="relative overflow-hidden">
        <img src={ASSETS.labBg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #060608 0%, rgba(6,6,8,0.7) 50%, #060608 100%)" }} />
        <div className="relative mx-auto max-w-[900px] px-5 py-20 text-center">
          <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            <span className="text-chrome">WE DON'T FORCE RESULTS.</span><br />
            <span className="text-holo">WE EARN THEM THROUGH PROCESS.</span>
          </h2>
          <p className="text-base text-[color:var(--chrome-2)] mt-6 max-w-xl mx-auto leading-relaxed">
            CardScience HQ is for collectors who want their cards, comics and collectibles handled thoughtfully.
          </p>
          <div className="flex flex-wrap justify-center gap-2.5 mt-6">
            {["No miracle claims.", "No magic chemicals.", "No aggressive shortcuts.", "No grade guarantees."].map((t) => (
              <span key={t} className="rounded-full border border-[color:var(--line)] px-4 py-2 text-sm text-[color:var(--chrome-2)]">{t}</span>
            ))}
          </div>
          <p className="text-[color:var(--chrome-1)] mt-6 max-w-xl mx-auto">
            Just experience, judgment, documentation and a process built around the collectible.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-9">
            <Link to="/how-it-works" data-testid="tc-see-how" className="btn-ghost rounded-xl px-7 py-4 text-sm inline-flex items-center justify-center gap-2">
              SEE HOW IT WORKS <ArrowRight size={16} />
            </Link>
            <Link to="/submit" data-testid="tc-submit" className="btn-holo rounded-xl px-7 py-4 text-sm inline-flex items-center justify-center gap-2">
              SUBMIT A COLLECTIBLE <ArrowRight size={16} />
            </Link>
          </div>
          <p className="font-script text-2xl text-[color:var(--chrome-1)] mt-10">
            If we don't believe we should touch it, we won't take your money.
          </p>
        </div>
      </div>
    </div>
  );
}
